import { Link } from "react-router";
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  Award,
  MoreHorizontal,
  Star,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/empty-state";
import { formatApiError } from "@/lib/format-api-error";
import { formatRelative } from "@/lib/utils";
import { CreateTierProgramDialog } from "./create-tier-program-dialog";
import { useArchiveTierProgram } from "./use-archive-program";
import { useSetDefaultProgram } from "./use-set-default-program";
import { useUpdateTierProgram } from "./use-update-program";
import { usePrograms, type AdminTierProgramListRow } from "./use-programs";

// Phase 7 sub-bucket 12 — admin tier programs list. Ports
// /app/admin/tiers/page.tsx with the sub-bucket 10/11 conventions:
//   - Shared <EmptyState> for loading-error + empty cases
//   - <CreateTierProgramDialog> Dialog (replaces legacy NewTierProgramDialog)
//   - Aggregate counts (tier_count + assignment_count) inline on the
//     list endpoint, not the legacy N+1 fan-out via listAssignmentsForProgram
//   - Row actions DropdownMenu (Make default / Archive / Restore / Open)
//
// The list reuses queryKeys.tiers.programs.list() — Phase 6 mutation hooks
// already invalidate this key, so create/archive/set-default/etc all refresh
// the list without per-hook wiring. No filtering/sorting toolbar in v1
// (matches legacy parity); the set is small (Bookboost starts with 1
// program) so the page renders the whole list inline.

export function TierProgramsListPage() {
  const query = usePrograms();
  const rows = query.data ?? [];

  return (
    <div>
      <header className="mb-7 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Admin
          </p>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">
            Tier programs
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Configure tier matrices, requirements and benefits. Assign
            partners to tiers and pick the default program for the legacy
            partners.tier mirror.
          </p>
        </div>
        <CreateTierProgramDialog />
      </header>

      {query.isLoading ? (
        <TableSkeleton />
      ) : query.isError ? (
        <EmptyState
          icon={ArrowLeft}
          title="Couldn't load tier programs"
          description={query.error?.message ?? "Try refreshing."}
        />
      ) : rows.length > 0 ? (
        <ProgramsTable rows={rows} />
      ) : (
        <EmptyState
          icon={Award}
          title="No tier programs yet"
          description="Create a tier program to start configuring matrices and assigning partners."
          action={<CreateTierProgramDialog variant="empty" />}
        />
      )}
    </div>
  );
}

function ProgramsTable({ rows }: { rows: AdminTierProgramListRow[] }) {
  return (
    <div className="overflow-hidden rounded-xl border bg-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/30 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            <th className="px-4 py-2.5">Name</th>
            <th className="px-4 py-2.5">Slug</th>
            <th className="px-4 py-2.5 text-right">Tiers</th>
            <th className="px-4 py-2.5 text-right">Partners assigned</th>
            <th className="px-4 py-2.5 text-right">Last updated</th>
            <th className="w-10" />
          </tr>
        </thead>
        <tbody className="divide-y divide-border/70">
          {rows.map((r) => (
            <ProgramRow key={r.id} row={r} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProgramRow({ row }: { row: AdminTierProgramListRow }) {
  const setDefault = useSetDefaultProgram();
  const archive = useArchiveTierProgram();
  const updateProgram = useUpdateTierProgram();

  const busy =
    setDefault.isPending || archive.isPending || updateProgram.isPending;

  function handleSetDefault() {
    if (row.is_default) return;
    setDefault.mutate(
      { id: row.id },
      {
        onSuccess: () => toast.success(`"${row.name}" is now the default program`),
        onError: (err) => toast.error(formatApiError(err, "Failed to set default")),
      },
    );
  }

  function handleArchive() {
    archive.mutate(
      { id: row.id },
      {
        onSuccess: () => toast.success("Program archived"),
        onError: (err) => toast.error(formatApiError(err, "Failed to archive")),
      },
    );
  }

  function handleRestore() {
    updateProgram.mutate(
      { id: row.id, patch: { is_archived: false } },
      {
        onSuccess: () => toast.success("Program restored"),
        onError: (err) => toast.error(formatApiError(err, "Failed to restore")),
      },
    );
  }

  return (
    <tr className="group transition-colors hover:bg-muted/30">
      <td className="px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to={`/admin/tiers/${row.id}`}
            className="font-medium text-foreground hover:text-accent"
          >
            {row.name}
          </Link>
          {row.is_default && (
            <span className="inline-flex items-center gap-1 rounded-full border border-accent/40 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-accent">
              <Star className="h-3 w-3" /> Default
            </span>
          )}
          {row.is_archived && (
            <span className="inline-flex items-center gap-1 rounded-full border border-border/70 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              <Archive className="h-3 w-3" /> Archived
            </span>
          )}
        </div>
        {row.description && (
          <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">
            {row.description}
          </p>
        )}
      </td>
      <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
        {row.slug}
      </td>
      <td className="px-4 py-3 text-right tabular-nums text-foreground/90">
        {row.tier_count}
      </td>
      <td className="px-4 py-3 text-right tabular-nums text-foreground/90">
        {row.assignment_count}
      </td>
      <td className="px-4 py-3 text-right text-xs text-muted-foreground">
        {formatRelative(row.updated_at)}
      </td>
      <td className="px-4 py-3 text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              aria-label={`Actions for ${row.name}`}
              disabled={busy}
              className="opacity-0 transition group-hover:opacity-100 data-[state=open]:opacity-100"
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <Link to={`/admin/tiers/${row.id}`}>Open editor</Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {!row.is_default && !row.is_archived && (
              <DropdownMenuItem onClick={handleSetDefault} disabled={busy}>
                <Star className="mr-2 h-3.5 w-3.5" /> Make default
              </DropdownMenuItem>
            )}
            {row.is_archived ? (
              <DropdownMenuItem onClick={handleRestore} disabled={busy}>
                <ArchiveRestore className="mr-2 h-3.5 w-3.5" /> Restore
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={handleArchive} disabled={busy}>
                <Archive className="mr-2 h-3.5 w-3.5" /> Archive
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  );
}

function TableSkeleton() {
  return (
    <div className="rounded-xl border bg-card">
      <div className="space-y-3 p-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex items-center gap-4">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="ml-auto h-4 w-12" />
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-4 w-16" />
          </div>
        ))}
      </div>
    </div>
  );
}
